import React from "react";

import { Column } from "primereact/column";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useParams } from "react-router-dom";

import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS,
  UnifiedDataTableColumnRenderers,
  UnifiedDataTableEmptyStateIconRenderers,
  UnifiedDataTableRowActionHandlers,
  UnifiedDataTableRowContext,
  UnifiedTableBlock,
  UnifiedTableColumn,
  UnifiedTableRecord,
  UnifiedTableRow,
  UnifiedTableSection,
} from "../../../shared/ui/data-table/model/data-table-contract";
import {
  TablePaginationHeaderControls,
  TablePaginatorFooter,
} from "../../../shared/ui/data-table/ui/table-paginator-footer";
import { renderTableSortIcon } from "../../../shared/ui/data-table/ui/table-sort-icon";
import {
  DownloadFileIcon,
  getDownloadExtension,
  getDownloadFormatLabel,
} from "../../../shared/ui/download-file/download-file";
import { SectionSidebarNav } from "../../../shared/ui/section-sidebar-nav/section-sidebar-nav";
import { ROUTES } from "../../../system/router/routes";
import * as Styled from "../../sectioned-data-table/ui/sectioned-data-table.styled";

interface UnifiedDataTableProps {
  record: UnifiedTableRecord;
  headerAction?: React.ReactNode;
  columnRenderers?: UnifiedDataTableColumnRenderers;
  emptyStateIconRenderers?: UnifiedDataTableEmptyStateIconRenderers;
  rowActions?: UnifiedDataTableRowActionHandlers;
  /* eslint-disable no-unused-vars */
  onRowClick?(row: UnifiedTableRow, context: UnifiedDataTableRowContext): void;
  isRowClickable?(
    row: UnifiedTableRow,
    context: UnifiedDataTableRowContext,
  ): boolean;
  /* eslint-enable no-unused-vars */
  useSharedSectionNav?: boolean;
  scrollContainerRef?: { current: HTMLElement | null };
}

interface TablePaginationState {
  first: number;
  rows: number;
}

const ROI_STATUS_COLUMN_WIDTH = 130;
const ROI_ACTIONS_COLUMN_WIDTH = 152;
const DEFAULT_SEARCH_PLACEHOLDER = "Search...";
const DEFAULT_DOWNLOAD_HREF = "#";

const toTableRow = (value: unknown): UnifiedTableRow =>
  value && typeof value === "object" ? (value as UnifiedTableRow) : {};

const getRendererKey = (column: UnifiedTableColumn) =>
  column.rendererId ?? column.key;

const isUrl = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }

  if (!value.includes(".") && !value.startsWith("http")) {
    return false;
  }

  try {
    const url = new URL(value.startsWith("http") ? value : `https://${value}`);

    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const asDisplayValue = (value: unknown) => {
  if (React.isValidElement(value)) {
    return value;
  }

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  return "—";
};

const asSearchableText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(asSearchableText).filter(Boolean).join(" ");
  }

  return "";
};

const getColumnWidth = (column: UnifiedTableColumn) => {
  if (column.width !== undefined) {
    return column.width;
  }

  switch (getRendererKey(column)) {
    case "roiStatus":
      return ROI_STATUS_COLUMN_WIDTH;
    case "roiActions":
      return ROI_ACTIONS_COLUMN_WIDTH;
    default:
      return undefined;
  }
};

const getColumnStyle = (column: UnifiedTableColumn) => {
  const width = getColumnWidth(column);
  const style: React.CSSProperties = {};

  if (width !== undefined) {
    const cssWidth = typeof width === "number" ? `${width}px` : width;

    style.width = cssWidth;
    style.minWidth = cssWidth;
    style.maxWidth = cssWidth;
  }

  if (column.align) {
    style.textAlign = column.align;
  }

  return Object.keys(style).length > 0 ? style : undefined;
};

const getColumnClassName = (column: UnifiedTableColumn) => {
  switch (getRendererKey(column)) {
    case "roiStatus":
      return "is-roi-status-column";
    case "roiActions":
      return "is-roi-actions-column";
    case "spacer":
      return "is-table-spacer-column";
    default:
      return undefined;
  }
};

const getDownloadFileName = (
  record: UnifiedTableRecord,
  format: string,
): string => {
  const moduleName = record.module ?? record.pageTitle ?? "records";

  return `${moduleName}.${getDownloadExtension(format)}`;
};

export const UnifiedDataTable = ({
  record,
  headerAction,
  columnRenderers,
  emptyStateIconRenderers,
  rowActions,
  onRowClick,
  isRowClickable,
  useSharedSectionNav = false,
  scrollContainerRef,
}: UnifiedDataTableProps) => {
  const { patientID } = useParams();
  const [tableSearchInputs, setTableSearchInputs] = React.useState<
    Record<string, string>
  >({});
  const [tableSearchQueries, setTableSearchQueries] = React.useState<
    Record<string, string>
  >({});
  const [tablePaginationState, setTablePaginationState] = React.useState<
    Record<string, TablePaginationState>
  >({});

  const navigationSections = React.useMemo(
    () =>
      (record.subnav ?? []).map((item) => ({
        id: item.sectionId,
        label: item.label,
      })),
    [record.subnav],
  );

  const resolveDefaultLink = React.useCallback(
    (columnKey: string, row: UnifiedTableRow) => {
      const encounterCode = String(row.encCode ?? "");

      switch (columnKey) {
        case "encDetails":
          if (!patientID || !encounterCode) {
            return "—";
          }

          return (
            <Styled.DetailsLink
              to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_HISTORY_REPORT.replace(
                ":patientID",
                patientID,
              ).replace(":encounterID", encounterCode)}
            >
              History & Physical Report
            </Styled.DetailsLink>
          );
        case "encLabsProcedures":
          if (!patientID || !encounterCode) {
            return "—";
          }

          return (
            <Styled.DetailsLink
              to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT.replace(
                ":patientID",
                patientID,
              ).replace(":encounterID", encounterCode)}
            >
              Labs & Procedures
            </Styled.DetailsLink>
          );
        case "encBilling":
          if (!patientID || !encounterCode) {
            return "—";
          }

          return (
            <Styled.DetailsLink
              to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_BILLING_REPORT.replace(
                ":patientID",
                patientID,
              ).replace(":encounterID", encounterCode)}
            >
              Billing Information
            </Styled.DetailsLink>
          );
        case "encScannedDocs":
          if (!patientID || !encounterCode) {
            return "—";
          }

          return (
            <Styled.DetailsLink
              to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_SCANNED_DOCS.replace(
                ":patientID",
                patientID,
              ).replace(":encounterID", encounterCode)}
            >
              Scanned Documents
            </Styled.DetailsLink>
          );
        default:
          return null;
      }
    },
    [patientID],
  );

  const resolveRoiActions = React.useCallback(
    (row: UnifiedTableRow) => {
      const roiStatus = String(row.roiStatus ?? "");

      if (roiStatus === "Draft") {
        return (
          <Styled.RoiControls>
            <Styled.RoiButton
              type="button"
              aria-label="Cancel request"
              title="Cancel request"
              onClick={(event) => {
                event.stopPropagation();
                rowActions?.cancel?.(row);
              }}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </Styled.RoiButton>
            <Styled.RoiButton
              type="button"
              aria-label="Edit request"
              title="Edit request"
              onClick={(event) => {
                event.stopPropagation();
                rowActions?.edit?.(row);
              }}
            >
              <i className="pi pi-pencil" aria-hidden="true" />
            </Styled.RoiButton>
          </Styled.RoiControls>
        );
      }

      if (roiStatus === "Completed") {
        return (
          <Styled.RoiControls>
            <Styled.RoiButton
              type="button"
              aria-label="Duplicate request"
              title="Duplicate request"
              onClick={(event) => {
                event.stopPropagation();
                rowActions?.duplicate?.(row);
              }}
            >
              <i className="pi pi-copy" aria-hidden="true" />
            </Styled.RoiButton>
            <Styled.RoiButton
              type="button"
              aria-label="View request"
              title="View request"
              onClick={(event) => {
                event.stopPropagation();
                rowActions?.view?.(row);
              }}
            >
              <i className="pi pi-eye" aria-hidden="true" />
            </Styled.RoiButton>
            <Styled.RoiButton
              type="button"
              aria-label="Download request"
              title="Download request"
              onClick={(event) => {
                event.stopPropagation();
                rowActions?.download?.(row);
              }}
            >
              <i className="pi pi-download" aria-hidden="true" />
            </Styled.RoiButton>
          </Styled.RoiControls>
        );
      }

      return null;
    },
    [rowActions],
  );

  const getSearchKeys = React.useCallback((table: UnifiedTableBlock) => {
    if (table.ui?.searchKeys && table.ui.searchKeys.length > 0) {
      return table.ui.searchKeys;
    }

    return table.columns
      .filter((column) => Boolean(column.header))
      .map((column) => column.key);
  }, []);

  const getFilteredRows = React.useCallback(
    (table: UnifiedTableBlock) => {
      const query = tableSearchQueries[table.id]?.trim().toLowerCase();

      if (!table.ui?.searchable || !query) {
        return table.rows;
      }

      const searchKeys = getSearchKeys(table);

      return table.rows.filter((rawRow) => {
        const row = toTableRow(rawRow);

        return searchKeys.some((key) =>
          asSearchableText(row[key]).toLowerCase().includes(query),
        );
      });
    },
    [getSearchKeys, tableSearchQueries],
  );

  const getRowsPerPageOptions = React.useCallback(
    (table: UnifiedTableBlock) =>
      table.ui?.rowsPerPageOptions && table.ui.rowsPerPageOptions.length > 0
        ? table.ui.rowsPerPageOptions
        : Array.from(DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS),
    [],
  );

  const getDefaultRowsPerPage = React.useCallback(
    (table: UnifiedTableBlock) =>
      table.ui?.defaultRowsPerPage ?? DEFAULT_TABLE_ROWS_PER_PAGE,
    [],
  );

  const buildRowContext = React.useCallback(
    (
      row: UnifiedTableRow,
      section: UnifiedTableSection,
      table: UnifiedTableBlock,
    ): UnifiedDataTableRowContext => ({
      row,
      rowIndex: table.rows.indexOf(row),
      section,
      table,
      record,
    }),
    [record],
  );

  const canClickRow = React.useCallback(
    (
      row: UnifiedTableRow,
      section: UnifiedTableSection,
      table: UnifiedTableBlock,
    ) => {
      const context = buildRowContext(row, section, table);

      if (isRowClickable) {
        return isRowClickable(row, context);
      }

      const roiStatus = row.roiStatus;

      return roiStatus === undefined || roiStatus === "Completed";
    },
    [buildRowContext, isRowClickable],
  );

  const renderCell = React.useCallback(
    (
      rawRow: unknown,
      column: UnifiedTableColumn,
      section: UnifiedTableSection,
      table: UnifiedTableBlock,
    ) => {
      const row = toTableRow(rawRow);
      const value = row[column.key];
      const rendererKey = column.rendererId ?? column.key;
      const customRenderer = columnRenderers?.[rendererKey];

      if (customRenderer) {
        return customRenderer({
          value,
          row,
          column,
          section,
          table,
          record,
        });
      }

      if (rendererKey === "roiActions") {
        return resolveRoiActions(row);
      }

      if (rendererKey === "roiStatus") {
        const status = String(value ?? "");

        return (
          <Styled.RoiStatus status={status}>{status || "—"}</Styled.RoiStatus>
        );
      }

      if (rendererKey === "docUrl" && isUrl(value)) {
        const href = value.startsWith("http") ? value : `https://${value}`;

        return (
          <Styled.DocLink href={href} target="_blank" rel="noopener noreferrer">
            <i className="pi pi-eye" aria-hidden="true" />
            View
          </Styled.DocLink>
        );
      }

      const linkCell = resolveDefaultLink(rendererKey, row);

      if (linkCell) {
        return linkCell;
      }

      return asDisplayValue(value);
    },
    [columnRenderers, record, resolveDefaultLink, resolveRoiActions],
  );

  const renderEmptyState = React.useCallback(
    (table: UnifiedTableBlock, message: string) => {
      const iconRendererId = table.ui?.emptyStateIconRendererId;
      const icon = iconRendererId
        ? emptyStateIconRenderers?.[iconRendererId]
        : null;

      return (
        <Styled.EmptyState>
          {icon ? <Styled.EmptyStateIcon>{icon}</Styled.EmptyStateIcon> : null}
          <Styled.EmptyStateMessage>{message}</Styled.EmptyStateMessage>
        </Styled.EmptyState>
      );
    },
    [emptyStateIconRenderers],
  );

  const tableContent = (
    <Styled.EntityContainer>
      {record.sections.map((section) => (
        <Styled.Entity key={section.id}>
          {record.sections.length > 1 ? (
            <Styled.EntityTitle id={section.id}>
              {section.title}
            </Styled.EntityTitle>
          ) : null}
          {section.tables.length > 0 ? (
            section.tables.map((table, tableIndex) => {
              const rowsPerPageOptions = getRowsPerPageOptions(table);
              const defaultRowsPerPage = getDefaultRowsPerPage(table);
              const shouldShowPaginator = table.ui?.showPaginator ?? true;
              const shouldShowSearch = Boolean(table.ui?.searchable);
              const downloadFormats = table.ui?.showTableDownloads
                ? table.ui?.downloadable ?? []
                : [];
              const shouldShowDownloads = downloadFormats.length > 0;
              const filteredRows = getFilteredRows(table);
              const paginationState = tablePaginationState[table.id];
              const currentRowsPerPage =
                paginationState?.rows ?? defaultRowsPerPage;
              const maxFirst =
                filteredRows.length > 0
                  ? Math.floor((filteredRows.length - 1) / currentRowsPerPage) *
                    currentRowsPerPage
                  : 0;
              const currentFirst = Math.min(
                paginationState?.first ?? 0,
                maxFirst,
              );
              const visibleRows = shouldShowPaginator
                ? filteredRows.slice(
                    currentFirst,
                    currentFirst + currentRowsPerPage,
                  )
                : filteredRows;
              const searchValue = tableSearchQueries[table.id] ?? "";
              const searchInputValue =
                tableSearchInputs[table.id] ?? searchValue;
              const hasHeaderAction = Boolean(headerAction && tableIndex === 0);
              const shouldRenderToolbar =
                shouldShowPaginator ||
                shouldShowDownloads ||
                shouldShowSearch ||
                hasHeaderAction;
              const emptyMessage =
                shouldShowSearch && searchValue.trim()
                  ? "No matching records found."
                  : table.ui?.emptyMessage ?? "No records found.";
              const shouldRenderEmptyPanel =
                filteredRows.length === 0 &&
                table.ui?.emptyStateVariant === "panel";
              const tableEmptyMessage = table.ui?.emptyStateIconRendererId
                ? renderEmptyState(table, emptyMessage)
                : emptyMessage;

              return (
                <Styled.TableWrapper key={table.id}>
                  {shouldRenderToolbar ? (
                    <Styled.TableActions>
                      {shouldShowPaginator ? (
                        <Styled.TableActionGroup>
                          <TablePaginationHeaderControls
                            first={currentFirst}
                            rows={currentRowsPerPage}
                            totalRecords={filteredRows.length}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onRowsChange={(rows) => {
                              setTablePaginationState((current) => ({
                                ...current,
                                [table.id]: {
                                  first: 0,
                                  rows,
                                },
                              }));
                            }}
                          />
                        </Styled.TableActionGroup>
                      ) : null}
                      <Styled.TableActionGroup>
                        {shouldShowDownloads ? (
                          <Styled.DownloadControls>
                            <span>Download as</span>
                            <Styled.DownloadOptions>
                              {downloadFormats.map((format) => {
                                const href =
                                  table.ui?.downloadLinks?.[format] ??
                                  (format === "xlsx"
                                    ? table.ui?.downloadLinks?.xls
                                    : undefined) ??
                                  DEFAULT_DOWNLOAD_HREF;

                                return (
                                  <Styled.DownloadLink
                                    key={format}
                                    href={href}
                                    download={getDownloadFileName(
                                      record,
                                      format,
                                    )}
                                    aria-label={`Download as ${getDownloadFormatLabel(format)}`}
                                  >
                                    <DownloadFileIcon format={format} />
                                  </Styled.DownloadLink>
                                );
                              })}
                            </Styled.DownloadOptions>
                          </Styled.DownloadControls>
                        ) : null}
                        {shouldShowSearch ? (
                          <Styled.TableSearch
                            onSubmit={(event) => {
                              event.preventDefault();

                              setTableSearchQueries((current) => ({
                                ...current,
                                [table.id]: searchInputValue,
                              }));
                              setTablePaginationState((current) => ({
                                ...current,
                                [table.id]: {
                                  first: 0,
                                  rows: currentRowsPerPage,
                                },
                              }));
                            }}
                          >
                            <Styled.TableSearchInput
                              type="text"
                              value={searchInputValue}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setTableSearchInputs((current) => ({
                                  ...current,
                                  [table.id]: event.target.value,
                                }));
                              }}
                              placeholder={
                                table.ui?.searchPlaceholder ??
                                DEFAULT_SEARCH_PLACEHOLDER
                              }
                              aria-label={
                                table.ui?.searchPlaceholder ??
                                DEFAULT_SEARCH_PLACEHOLDER
                              }
                            />
                            <Styled.TableSearchIcon
                              type="submit"
                              className="pi pi-search"
                              aria-label="Search"
                            />
                          </Styled.TableSearch>
                        ) : null}
                        {hasHeaderAction ? headerAction : null}
                      </Styled.TableActionGroup>
                    </Styled.TableActions>
                  ) : null}
                  {shouldRenderEmptyPanel ? (
                    <Styled.EmptyStatePanel>
                      {emptyMessage}
                    </Styled.EmptyStatePanel>
                  ) : (
                    <Styled.Table
                      className={
                        record.module ? `is-${record.module}-table` : undefined
                      }
                      value={visibleRows}
                      scrollable
                      removableSort
                      rowHover={table.ui?.rowHover ?? Boolean(onRowClick)}
                      stripedRows={table.ui?.stripedRows}
                      sortIcon={renderTableSortIcon}
                      tableStyle={{ minWidth: "100%" }}
                      emptyMessage={tableEmptyMessage}
                      rowClassName={
                        onRowClick
                          ? (rawRow) => {
                              const row = toTableRow(rawRow);

                              return canClickRow(row, section, table)
                                ? "is-clickable-row"
                                : "";
                            }
                          : undefined
                      }
                      onRowClick={
                        onRowClick
                          ? (event) => {
                              const row = toTableRow(event.data);
                              const context = buildRowContext(
                                row,
                                section,
                                table,
                              );

                              if (!canClickRow(row, section, table)) {
                                return;
                              }

                              onRowClick(row, context);
                            }
                          : undefined
                      }
                    >
                      {table.columns.map((column) => (
                        <Column
                          key={column.key}
                          field={column.key}
                          header={column.header}
                          sortable={Boolean(column.sortable)}
                          frozen={Boolean(column.frozen)}
                          alignFrozen={column.frozen}
                          style={getColumnStyle(column)}
                          bodyStyle={getColumnStyle(column)}
                          headerStyle={getColumnStyle(column)}
                          bodyClassName={getColumnClassName(column)}
                          headerClassName={getColumnClassName(column)}
                          body={(rawRow) => (
                            <Styled.CellContent $align={column.align}>
                              {renderCell(rawRow, column, section, table)}
                            </Styled.CellContent>
                          )}
                        />
                      ))}
                    </Styled.Table>
                  )}
                  {shouldShowPaginator ? (
                    <TablePaginatorFooter
                      first={currentFirst}
                      rows={currentRowsPerPage}
                      totalRecords={filteredRows.length}
                      rowsPerPageOptions={rowsPerPageOptions}
                      onPageChange={(event: PaginatorPageChangeEvent) => {
                        setTablePaginationState((current) => ({
                          ...current,
                          [table.id]: {
                            first: event.first,
                            rows: event.rows,
                          },
                        }));
                      }}
                    />
                  ) : null}
                </Styled.TableWrapper>
              );
            })
          ) : (
            <Styled.NoData>No data available</Styled.NoData>
          )}
        </Styled.Entity>
      ))}
    </Styled.EntityContainer>
  );

  return (
    <Styled.InnerContainer>
      {navigationSections.length > 0 &&
        (useSharedSectionNav ? (
          <SectionSidebarNav
            ariaLabel="Section navigation"
            sections={navigationSections}
            scrollContainerRef={scrollContainerRef}
          />
        ) : (
          <Styled.Submenu>
            {navigationSections.map((item) => (
              <a key={item.id} href={`#${item.id}`}>
                {item.label}
              </a>
            ))}
          </Styled.Submenu>
        ))}
      <Styled.EntityScroller
        ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      >
        {tableContent}
      </Styled.EntityScroller>
    </Styled.InnerContainer>
  );
};
